import { setContext, getContext } from "svelte";
import { getAbsoluteUrl } from "$utils/urls.js";
import { parseDate, formatQuarter, formatYear, parseYear } from "$utils/dates.js";
import { getName, getAllStates } from "$utils/geoNames.js";
import { DEFAULT_PLAN_TYPE } from "$utils/consts.js";
import metadata from "$data/metadata.json";
import Papa from "papaparse";

/** @typedef {import("$utils/types").DataObject} DataObject */

export class ToolData {
  meta = metadata;
  /** @type {DataObject[] | undefined} */
  rawData = $state();
  /** @type {string | null} */
  activeGeo = $state("00");
  activeTime = $state("2023");
  activeMetric = $state("numberofrx");
  activeFormat = $derived.by(() => this.getMetricFormat(this.activeMetric));
  activeMapCategory = $state("all non-NRT & NRT");
  activeUtilization = $state("all");
  activeFrequency = $state("quarterly");
  activeMedType = $state("all");
  highlightCategory = $state(null);
  showSubcategories = $state(false);
  activeCategories = $derived(
    this.showSubcategories
      ? metadata.medication_groups[1].types_included
      : metadata.medication_groups[0].types_included
  );
  quarterList = $derived.by(() => this.getAllQuarters());
  yearList = $derived.by(() => this.getAllYears());
  stateList = $derived.by(() => this.getStateList());
  timeList = $derived.by(() => this.getAllTimes());
  chartData = $derived.by(() => this.getChartData());
  mapData = $derived.by(() => this.getMapData());
  statePlans = $derived.by(() => this.getStatePlans());
  availablePlans = $derived(this.statePlans[this.activeGeo])
  ticks = $derived.by(() => this.getTicks());

  async fetchData() {
    const res = await fetch(getAbsoluteUrl("/data/medicaid_tnd_rx.csv"));
    if (!res.ok) {
      throw new Error("Failed to fetch data");
    }
    const text = await res.text();
    const processed = Papa.parse(text, {
      header: true,
      dynamicTyping: false,
      skipEmptyLines: true,
      transform: this.transformValue
    });
    this.rawData = processed.data;
  }

  transformValue(value, field) {
    // parse NA strings as null
    if (value === "NA") {
      return null;
    }
    // parse dates as date objects
    if (field === "date") {
      return parseDate(value);
    }
    // make sure geoid is a string
    if (field === "geoid") {
      return value;
    }
    // convert numeric fields to numbers
    if (!isNaN(+value)) {
      return +value;
    }
    return value;
  }

  /**
   * @returns {string[]}
   */
  getAllQuarters() {
    if (!this.rawData || this.rawData.length === 0) return [];

    return Array.from(
      new Set(this.rawData.filter((d) => d.quarter).map((d) => formatQuarter(d.date)))
    );
  }

  /**
   * @returns {number[]}
   */
  getAllYears() {
    if (!this.rawData || this.rawData.length === 0) return [];

    return Array.from(new Set(this.rawData.filter((d) => !d.quarter).map((d) => d.year)));
  }

  /**
   * @returns {string[]}
   */
  getAllTimes() {
    if (!this.rawData || this.rawData.length === 0) return [];
    return Array.from(
      new Set(
        this.rawData.map((d) => {
          if (d.quarter) return formatQuarter(d.date);
          return d.year.toString();
        })
      )
    ).reverse();
  }

  /**
   *@returns {{label: string, value: string}[]}
   */
  getStateList() {
    if (!this.rawData || this.rawData.length === 0) return [];
    const geoids = Array.from(
      new Set(this.rawData?.filter(({ geoid }) => geoid)?.map(({ geoid }) => geoid))
    );
    return geoids.map((geoid) => ({ value: geoid, label: getName(geoid) }));
  }

  getChartData() {
    if (!this.rawData) return [];

    // cache categories once per check
    const categories = this.activeCategories;

    const filteredData = this.rawData.filter((d) => {
      // check geo
      const geoMatch = d.geoid === this.activeGeo;

      // check Utilization
      const utilizationMatch = d.utilizationtype === this.activeUtilization;

      // check Category
      const categoryMatch = categories.includes(d.product_cat);

      // check fequency
      const frequencyMatch =
        (this.activeFrequency === "annual" && !d.quarter) ||
        (this.activeFrequency === "quarterly" && d.quarter);

      // check brand
      const brandMatch = this.activeMedType == d.medtype;

      // const
      return geoMatch && utilizationMatch && categoryMatch && frequencyMatch && brandMatch;
    });

    let resultsByTime = new Map();
    for (const entry of filteredData) {
      const timeKey = entry.quarter ? formatQuarter(entry.date) : formatYear(entry.date);
      // if an entry exists already, add this as a new category and update existing entry
      if (resultsByTime.has(timeKey)) {
        const existing = resultsByTime.get(timeKey);
        existing[entry.product_cat] = entry[this.activeMetric];
        resultsByTime.set(timeKey, existing);
      } else {
        const newEntry = { date: entry.date };
        newEntry[entry.product_cat] = entry[this.activeMetric];
        resultsByTime.set(timeKey, newEntry);
      }
      // loop through all activeCategories and makes sure they exist
      for (const cat of categories) {
        if (!resultsByTime.get(timeKey)[cat]) {
          resultsByTime.get(timeKey)[cat] = 0;
        }
      }
    }
    return Array.from(resultsByTime.values());
  }
  formatTime(d) {
    if (d.quarter) {
      return formatQuarter(d.date);
    }
    return formatYear(d.date);
  }

  getMapData() {
    if (!this.rawData) return [];
    return this.rawData
      .filter((d) => {
        // exclude national summary
        if (d.geoid == "00") return false;
        // check time
        const timeMatch = this.formatTime(d) == this.activeTime;
        // check Utilization
        const utilizationMatch = d.utilizationtype === this.activeUtilization;
        // check Category matches exactly
        const categoryMatch = this.activeMapCategory === d.product_cat;
        // check brand
        const brandMatch = this.activeMedType === d.medtype;

        return timeMatch && utilizationMatch && categoryMatch && brandMatch;
      })
      .map((d) => {
        return {
          map_id: d.geoid,
          value: d[this.activeMetric]
        };
      });
  }

  getMetricFormat(metric) {
    const metricInfo = this.getMetricMeta(metric);
    return metricInfo?.unit_type || "INT";
  }

  getMetricMeta(metric) {
    const metricInfo = this.meta.metrics.find((m) => m.metric === metric);
    return metricInfo;
  }

  getMetricLabel(metric) {
    const metricInfo = this.getMetricMeta(metric);
    return metricInfo?.full_name || metric;
  }

  /**
   * @param {string} cat
   * @returns {string}
   **/
  formatCategory(cat) {
    let catMeta = this.meta.product_cats.find(({ category }) => category === cat);
    if (!catMeta) {
      console.warn(`No metadata found for category: ${cat}`);
      return cat;
    }
    return catMeta.label;
  }

  getMapCategories() {
    return ["all non-NRT & NRT", "all non-NRT", "all NRT"];
  }

  getBrandLabel(brand) {
    let brandMeta = this.meta.brands.find(({ value }) => value === brand);
    return brandMeta.label;
  }

  getStatePlans() {
    let result = {};
    for (const state of this.meta.available_plans) {
      result[state.geoid] = state.available_plans;
    }
    return result;
  }

  getTicks() {
    return this.yearList.map(parseYear);
  }
}

const contextKey = Symbol("ToolData");

export function createToolData() {
  let toolData = new ToolData();
  setContext(contextKey, toolData);
  return toolData;
}

export function useToolData() {
  return getContext(contextKey);
}
